import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button } from "rsuite";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PropertyStoreContext } from "../../../../contexts/PropertyStoreContext";
import {
  getDerivedPromoDetail,
  updateDerivedPromo,
  updateDerivedPromoStatus,
} from "../../../../api";
import {
  extractCalculationType,
  mapDerivedPromoDetailToPromo,
  mapPromoToUpdateDerivedPayload,
} from "../../../../utils/derivedRatesMapper";
import StatusToggle from "../../../common/StatusToggle";
import HTLoader from "../../../common/ht-loader/HTLoader";
import PromoExpiryWindowCell from "./PromoExpiryWindowCell";
import {
  buildPromoDeepLink,
  PROMO_EXPIRY_SOURCE,
} from "../../../../constants/promoExpiry";
import {
  MODAL_CLOSE_LABEL,
  MODAL_COLUMNS,
  MODAL_INACTIVE_LABEL,
  MODAL_MANAGED_LABEL,
  MODAL_NA_LABEL,
  MODAL_SAVED_LABEL,
  MODAL_SAVE_LABEL,
  MODAL_SUBTITLE,
  MODAL_TITLE,
} from "../promoExpiryConstants";
import {
  adjustmentLabel,
  mergeWindowEdits,
  promoWindows,
  compactStatusPillLabel,
  validateWindow,
} from "../promoExpiryUtils";
import "./PromoExpiryReviewModal.scss";

/**
 * Review Your Promotions — inline date editing for every promo on the card.
 *
 * Opened by the card's "Review & Extend"; the card stays mounted behind it, as CampaignCard does for its
 * review modal.
 *
 * Detail is fetched per promo on open rather than carried in the card payload. An edit form must show
 * live values, and the card's rows are written once a day — only their expiry date is validated fresh, so
 * start dates, the second window, the adjustment and the status would all be stale.
 */
const PromoExpiryReviewModal = ({ promos, onClose, onRefresh }) => {
  const navigate = useNavigate();
  const { selectedProperty } = React.useContext(PropertyStoreContext);
  const propertyId = selectedProperty?.propertyId;

  const [details, setDetails] = useState({});
  const [edits, setEdits] = useState({});
  const [rowState, setRowState] = useState({});

  /**
   * ⚠ Merges into the row's existing state; never replaces it.
   *
   * Each row carries independent flags — `deactivated`, `saved`, `saving`, `error` — and replacing the
   * object drops the ones the current handler does not mention. Saving a row after deactivating it used
   * to write `{ saving: true }` over `{ deactivated: true }`, which flipped the status toggle back to
   * checked for a promo that was already INACTIVE.
   */
  const setRowPatch = (promoId, patch) =>
    setRowState((prev) => ({
      ...prev,
      [promoId]: { ...(prev[promoId] || {}), ...patch },
    }));

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    // Fired in parallel — bounded by the card's max-items, and behind a user click.
    promos.forEach((promo) => {
      getDerivedPromoDetail(promo.promoId, propertyId)
        .then((response) => {
          if (cancelled) return;
          const raw = response?.data;
          // calculationType is read from the RAW detail, not the mapped promo — it lives in
          // derivedRates[].priceConfig, which the mapper folds away. The update payload needs it, and
          // guessing a default here would silently rewrite the promo's price basis on save.
          setDetails((prev) => ({
            ...prev,
            [promo.promoId]: {
              promo: mapDerivedPromoDetailToPromo(raw),
              calculationType: extractCalculationType(raw),
            },
          }));
        })
        .catch(() => {
          if (cancelled) return;
          // One failed detail must not block the other rows.
          setRowPatch(promo.promoId, {
            error: "Could not load this promotion.",
          });
        });
    });
    return () => {
      cancelled = true;
    };
  }, [promos, propertyId]);

  const setEdit = (promoId, patch) =>
    setEdits((prev) => ({
      ...prev,
      [promoId]: { ...(prev[promoId] || {}), ...patch },
    }));

  const handleSave = async (promo) => {
    const detail = details[promo.promoId];
    if (!detail) return;
    const edit = edits[promo.promoId] || {};
    const stored = promoWindows(detail.promo);
    const bookingStart = edit.bookingStartDate ?? stored.booking.start;

    // ⚠ Guarded on the window's existence, exactly as the stay window below. A promo can carry stay
    // dates and no booking window at all; validating the absent one returned "An end date is required."
    // on every save, so such a promo could never be saved from this modal at all.
    //
    // Keyed on the END date to match both the stay guard and the N/A cells, so the rule for "is this
    // window here" is the same one the table renders from. Do not make one side of this asymmetric
    // again — that asymmetry is the whole defect.
    const bookingError = stored.booking.end
      ? validateWindow({
          startDate: bookingStart,
          endDate: edit.bookingEndDate ?? stored.booking.end,
          originalStartDate: stored.booking.start,
          isStay: false,
        })
      : null;
    const stayError = stored.stay.end
      ? validateWindow({
          startDate: edit.stayStartDate ?? stored.stay.start,
          endDate: edit.stayEndDate ?? stored.stay.end,
          originalStartDate: stored.stay.start,
          bookingStartDate: bookingStart,
          isStay: true,
        })
      : null;

    const error = bookingError || stayError;
    if (error) {
      setRowPatch(promo.promoId, { error });
      return;
    }

    setRowPatch(promo.promoId, { saving: true, error: null });
    try {
      // ⚠ Goes through mapPromoToUpdateDerivedPayload, the same builder the create-promo edit flow uses.
      // The endpoint REPLACES the promo configuration, so posting the promo object directly would drop
      // rateRoomSelections, priceConfig and status and wipe the promo's setup.
      const payload = mapPromoToUpdateDerivedPayload(
        mergeWindowEdits(detail.promo, edit),
        detail.calculationType
      );
      await updateDerivedPromo(promo.promoId, propertyId, payload);
      // The row stays put with its new dates — transient UI only. The promo is already re-scored in the
      // index, so the next fetch drops it from the card entirely; there is nothing to persist here.
      setRowPatch(promo.promoId, { saving: false, saved: true });
    } catch {
      setRowPatch(promo.promoId, {
        saving: false,
        error: "Could not save this promotion.",
      });
      toast.error("Failed to save promotion");
    }
  };

  const handleDeactivate = async (promo) => {
    setRowPatch(promo.promoId, { saving: true, error: null });
    try {
      await updateDerivedPromoStatus(promo.promoId, propertyId, "INACTIVE");
      setRowPatch(promo.promoId, { saving: false, deactivated: true });
    } catch {
      setRowPatch(promo.promoId, {
        saving: false,
        error: "Could not deactivate this promotion.",
      });
      toast.error("Failed to deactivate promotion");
    }
  };

  // Hand-off rather than a second inline editor, via the URL rather than
  // location.state. The promotions page reads ?promoId= and strips it once the
  // drawer closes. A URL is what makes the destination shareable and, more to
  // the point, is what routes this through the page's permission gate and the
  // expiring_promo_cta_clicked event — location.state bypassed both, and left
  // the address bar reading a bare /promotions.
  const handleOpenPromo = (promo) =>
    navigate(buildPromoDeepLink(promo.promoId, PROMO_EXPIRY_SOURCE.CARD));

  /**
   * ⚠ Refetches the action-center feeds on the way out.
   *
   * Nothing in this modal writes back to the card behind it, and a saved row is deliberately left in
   * place while the modal is open. Without this, closing returns the user to a card still listing the
   * promo they just extended — which reads as the save having failed.
   */
  const handleClose = () => {
    onClose();
    onRefresh?.();
  };

  const rows = useMemo(() => promos, [promos]);

  // Details are fetched per row in parallel, and until the first one lands the
  // table is a set of named rows with every other cell blank — which reads as
  // "this promo has no dates" rather than "still loading". Show the loader only
  // while there is genuinely nothing to show; once any row resolves the table
  // appears and the stragglers keep their existing per-cell loading state, so a
  // slow row cannot hold up the ones that are ready.
  const isInitialLoading =
    rows.length > 0 &&
    rows.every(
      (promo) => !details[promo.promoId] && !rowState[promo.promoId]?.error
    );

  return (
    <Modal
      className="promo-expiry-modal"
      open
      size="lg"
      backdrop="static"
      onClose={handleClose}
    >
      <Modal.Header>
        <Modal.Title>{MODAL_TITLE}</Modal.Title>
        <p className="promo-expiry-modal-subtitle">{MODAL_SUBTITLE}</p>
      </Modal.Header>

      <Modal.Body>
        {isInitialLoading ? (
          <div
            className="promo-expiry-modal-loading"
            data-testid="promo-expiry-modal-loading"
          >
            <HTLoader loaderText="" />
          </div>
        ) : (
          <table className="promo-expiry-modal-table">
            <thead>
              <tr>
                {MODAL_COLUMNS.map((column) => (
                  <th key={column}>{column}</th>
                ))}
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((promo) => {
                const detail = details[promo.promoId];
                const state = rowState[promo.promoId] || {};
                const edit = edits[promo.promoId] || {};
                const loading = !detail && !state.error;
                const stored = promoWindows(detail?.promo);
                // ⚠ GC-created promos are read-only here, exactly as the create-promo flow already makes
                // them. Not a UX nicety: their discountVO maps to an ARRAY, mapPromoToUpdateDerivedPayload
                // reads it as a single object, so priceDelta comes out 0 for all seven days — and since the
                // endpoint REPLACES the configuration, saving would rewrite a live promo as a flat 0%
                // discount. The row still renders so the hotelier can see the expiry; the name link hands
                // off to the promotions page, which shows the same managed-promo banner.
                const managed =
                  detail?.promo?._derivedRateMeta?.isGcCreated === true;

                return (
                  <tr
                    key={promo.promoId}
                    data-testid={`promo-expiry-row-${promo.promoId}`}
                  >
                    <td>
                      <button
                        type="button"
                        className="promo-expiry-modal-name"
                        onClick={() => handleOpenPromo(promo)}
                      >
                        {promo.promoName}
                      </button>
                    </td>

                    <td>
                      {/* The same StatusToggle the Promotions table uses, so the control reads
                        identically on both surfaces. Its label slot carries the days-left text
                        rather than Active/Inactive — on this card the urgency is the point, and
                        every row here is active by definition. Once deactivated it falls back to
                        "Inactive", because "3d left" beside an off switch is a contradiction. */}
                      <StatusToggle
                        checked={!state.deactivated}
                        // ⚠ One-way. Only ACTIVE promos are ever indexed, so the modal can never show an
                        // inactive one and never requests ACTIVE. Disabled once used — an undo would be a
                        // reactivation, which the API rejects for expired promos but allows for unexpired
                        // ones, so it would work on some rows and error on others.
                        disabled={state.deactivated || state.saving || managed}
                        onChange={() => handleDeactivate(promo)}
                        label={
                          state.deactivated
                            ? MODAL_INACTIVE_LABEL
                            : compactStatusPillLabel(promo.daysLeft)
                        }
                        // Names the promo, because the visible label is a days-left count that several
                        // rows can share.
                        ariaLabel={`Deactivate ${promo.promoName}`}
                      />
                    </td>

                    <td className="promo-expiry-modal-adjustment">
                      {loading ? "" : adjustmentLabel(detail?.promo)}
                    </td>

                    <td>
                      {/* N/A when the promo has no booking window, mirroring the stay column. Empty
                        pickers here invited a half-filled window the save path then had to reject,
                        and this modal extends existing windows rather than adding new ones. */}
                      {!loading && !stored.booking.end ? (
                        MODAL_NA_LABEL
                      ) : (
                        <PromoExpiryWindowCell
                          loading={loading}
                          readOnly={managed}
                          startDate={
                            edit.bookingStartDate ?? stored.booking.start
                          }
                          endDate={edit.bookingEndDate ?? stored.booking.end}
                          highlight={promo.daysLeft < 0 ? "expired" : null}
                          onChange={(patch) =>
                            setEdit(promo.promoId, {
                              bookingStartDate: patch.startDate,
                              bookingEndDate: patch.endDate,
                            })
                          }
                        />
                      )}
                    </td>

                    <td>
                      {!loading && !stored.stay.end ? (
                        MODAL_NA_LABEL
                      ) : (
                        <PromoExpiryWindowCell
                          loading={loading}
                          readOnly={managed}
                          startDate={edit.stayStartDate ?? stored.stay.start}
                          endDate={edit.stayEndDate ?? stored.stay.end}
                          minDate={
                            edit.bookingStartDate ?? stored.booking.start
                          }
                          onChange={(patch) =>
                            setEdit(promo.promoId, {
                              stayStartDate: patch.startDate,
                              stayEndDate: patch.endDate,
                            })
                          }
                        />
                      )}
                    </td>

                    <td className="promo-expiry-modal-revenue">
                      {promo.revenue === null || promo.revenue === undefined
                        ? ""
                        : `$${Number(promo.revenue).toLocaleString()}`}
                    </td>

                    <td>
                      {managed ? (
                        <span className="promo-expiry-modal-managed">
                          {MODAL_MANAGED_LABEL}
                        </span>
                      ) : state.saved ? (
                        <span className="promo-expiry-modal-saved">
                          {MODAL_SAVED_LABEL}
                        </span>
                      ) : (
                        <Button
                          appearance="primary"
                          className="promo-expiry-modal-save"
                          // Disabled once deactivated: extending the dates of a promo the user just
                          // retired is contradictory, and the save would push status ACTIVE straight
                          // back over the INACTIVE that was just written.
                          disabled={
                            loading || state.saving || state.deactivated
                          }
                          onClick={() => handleSave(promo)}
                        >
                          {MODAL_SAVE_LABEL}
                        </Button>
                      )}
                      {state.error && (
                        <span className="promo-expiry-modal-error">
                          {state.error}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          appearance="ghost"
          className="promo-expiry-modal-close"
          onClick={handleClose}
          data-testid="promo-expiry-modal-close"
        >
          {MODAL_CLOSE_LABEL}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

PromoExpiryReviewModal.propTypes = {
  promos: PropTypes.arrayOf(
    PropTypes.shape({
      recommendationId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      promoId: PropTypes.string.isRequired,
      promoName: PropTypes.string,
      daysLeft: PropTypes.number,
      revenue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
};

export default PromoExpiryReviewModal;
